import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Typography, CircularProgress } from '@mui/material'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useQueryClient } from '@tanstack/react-query'
import type { UUID } from 'crypto'
import { useApplicationsByNeed } from '@/utils/hooks/applications'
import { useOnboardingV2 } from '../OnboardingContext'
import { useStep } from '../hooks/useStep'
import { useToken } from '../hooks/useToken'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCompleteProgress } from '../hooks/useCompleteProgress'
import { COLORS } from '../theme'
import { warningBoxSx } from '../styles'

export default function DraftComplete() {
  const { orgUuid, fundingNeedId, applicationId, setApplicationId } =
    useOnboardingV2()
  const { getToken } = useToken()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const completeProgressMutation = useCompleteProgress()

  const completedOnboarding =
    completeProgressMutation.isSuccess || completeProgressMutation.isError
  const completedRef = useRef(false)

  const { data: applications } = useApplicationsByNeed(
    token || null,
    (orgUuid as UUID) ?? null,
    fundingNeedId,
  )

  const resolvedAppId =
    applicationId ?? (applications?.[0]?.id ? String(applications[0].id) : null)

  useEffect(() => {
    if (!applicationId && resolvedAppId) {
      setApplicationId(resolvedAppId)
    }
  }, [applicationId, resolvedAppId, setApplicationId])

  // Mark onboarding complete once on mount
  useEffect(() => {
    if (completedRef.current || !orgUuid) return
    completedRef.current = true

    getToken().then((t) => {
      completeProgressMutation.mutate({ token: t, orgUuid })
    })
  }, [orgUuid])

  const handleViewApplication = () => {
    // Optimistically mark onboarding complete in cache so GuardedOutlet doesn't
    // redirect back to /onboarding before the background refetch finishes.
    queryClient.setQueryData(
      ['onboarding-status', orgUuid],
      (old: Record<string, unknown> | null | undefined) =>
        old ? { ...old, is_complete: true } : { is_complete: true },
    )
    queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
    queryClient.invalidateQueries({ queryKey: ['onboardingData'] })

    if (fundingNeedId && resolvedAppId) {
      navigate(`/applications/${fundingNeedId}/${resolvedAppId}`, {
        replace: true,
      })
    } else if (fundingNeedId) {
      navigate(`/applications/${fundingNeedId}`, { replace: true })
    } else {
      navigate('/', { replace: true })
    }
  }

  useStep({
    isValid: completedOnboarding && !!resolvedAppId,
    beforeNext: async () => {
      handleViewApplication()
      return false // prevent default goNext — we navigate manually
    },
    continueLabel: 'Continue',
    hideBack: false,
  })

  if (!completedOnboarding || !resolvedAppId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: COLORS.primary }} />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 6,
        px: 2,
      }}
    >
      <img
        src="/fundsorter-straight-arm.webp"
        alt="Fundsorter"
        style={{ height: 80, width: 'auto', marginBottom: 24 }}
      />
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, color: COLORS.primary, mb: 2 }}
      >
        Onboarding complete!
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: COLORS.labelText, maxWidth: 520, lineHeight: 1.6, mb: 2 }}
      >
        Your application draft is ready. You're now being taken to your drafted
        application where you can review and edit the answers Fundsorter has
        generated for you.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          ...warningBoxSx,
          borderRadius: 3,
          p: 2.5,
          maxWidth: 520,
          textAlign: 'left',
          mb: 2,
        }}
      >
        <InfoOutlinedIcon
          sx={{
            color: COLORS.warningIcon,
            fontSize: 22,
            mt: 0.25,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{ color: COLORS.labelText, lineHeight: 1.6 }}
        >
          <strong>Important:</strong> Fundsorter does not submit applications to
          funders for you. It is up to you to review the draft, make any
          changes, and submit it directly to the funder.
        </Typography>
      </Box>

      <Typography
        variant="body1"
        sx={{ color: COLORS.labelText, maxWidth: 520, lineHeight: 1.6, mb: 4 }}
      >
        Once you get up and running with Fundsorter, you can create multiple
        funding needs and scan for more matches. Each funding need can have its
        own set of drafted applications.
      </Typography>
    </Box>
  )
}
